import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useEffect } from "react";
import { X } from "lucide-react";
import { useLanguage } from "@/context/language-context";
import { useLenis } from "@/components/smooth-scroll";

interface AboutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type AboutSection =
  | { type: "lead"; text: string }
  | { type: "body"; text: string }
  | { type: "heading"; text: string }
  | { type: "education"; title: string; subtitle: string; text: string }
  | { type: "closing"; text: string };

function AboutSectionBlock({ section }: { section: AboutSection }) {
  switch (section.type) {
    case "lead":
      return (
        <p className="text-2xl leading-snug font-light tracking-tight text-foreground md:text-3xl">
          {section.text}
        </p>
      );
    case "body":
      return (
        <p className="text-base leading-relaxed text-muted-foreground md:text-lg">{section.text}</p>
      );
    case "heading":
      return (
        <div className="pt-4">
          <span className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground/50 uppercase">
            01
          </span>
          <h3 className="mt-2 text-xl font-bold tracking-tight text-foreground uppercase md:text-2xl">
            {section.text}
          </h3>
        </div>
      );
    case "education":
      return (
        <div className="space-y-2 rounded-2xl border border-border/50 bg-secondary/5 p-5 md:p-6">
          <h4 className="text-lg font-semibold tracking-tight text-foreground md:text-xl">
            {section.title}
          </h4>
          <p className="font-mono text-xs tracking-widest text-muted-foreground uppercase md:text-sm">
            {section.subtitle}
          </p>
          <p className="pt-1 text-sm leading-relaxed text-muted-foreground md:text-base">
            {section.text}
          </p>
        </div>
      );
    case "closing":
      return (
        <p className="border-t border-border/50 pt-8 text-base leading-relaxed text-foreground/70 italic md:text-lg">
          {section.text}
        </p>
      );
  }
}

export function AboutModal({ open, onOpenChange }: AboutModalProps) {
  const { content, rawContent } = useLanguage();
  const lenis = useLenis();
  const sections = rawContent.about.full_sections as AboutSection[];

  useEffect(() => {
    if (open) {
      lenis?.stop();
    } else {
      lenis?.start();
    }
  }, [open, lenis]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="fixed inset-0 top-0 left-0 z-modal flex h-[100dvh] w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 bg-background p-0 shadow-none sm:inset-auto sm:top-[50%] sm:left-[50%] sm:h-auto sm:max-h-[90vh] sm:w-[95vw] sm:max-w-3xl sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-2xl sm:border sm:border-border/50 sm:bg-background/95 sm:shadow-lg sm:backdrop-blur-xl"
      >
        <header className="sticky top-0 z-20 flex shrink-0 items-center justify-between gap-4 border-b border-border/50 bg-background px-4 py-4 sm:bg-background/95 sm:px-6 sm:py-5 sm:backdrop-blur-xl">
          <DialogHeader className="min-w-0 flex-1 gap-0 text-left">
            <DialogTitle className="truncate text-2xl leading-none font-bold tracking-tighter uppercase sm:text-3xl md:text-4xl">
              {content.about.title}
            </DialogTitle>
          </DialogHeader>
          <DialogClose className="focus-ring flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-background text-foreground ring-2 ring-black ring-offset-2 ring-offset-background transition-colors hover:bg-foreground hover:text-background dark:ring-white">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </header>

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-6 sm:px-6 sm:py-8 md:px-10"
          data-lenis-prevent="true"
        >
          <div className="flex flex-col gap-6 md:gap-8">
            {sections.map((section, index) => (
              <AboutSectionBlock key={index} section={section} />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
